import "core-js/modules/es.string.split";
import "core-js/modules/web.dom-collections.iterator";
import "core-js/modules/es.string.split";
import "core-js/modules/web.dom-collections.iterator";
/**
 * Section: Menu drawer
 * -----------------------------------------------------------------------------
 * Scripts to initiate menu drawer toggle and also display on admin.
 *
 * @namespace menuDrawer
 */

import Cookies from 'js-cookie';
import { register } from '@shopify/theme-sections';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import Modal from '~/components/modal';
import cssClasses from '~/helpers/cssClasses';
import { on, getSiblings } from '~/helpers/utils';
import Toggle from '~/components/toggle';
import Redirects from '~/helpers/redirects';
import Currency from '~/helpers/currency';
/**
 * DOM selectors.
 */

const selectors = {
  body: '[js-menu-drawer="body"]',
  documentBody: 'body',
  tier2MenuToggle: '[js-menu-drawer="tier2MenuToggle"]',
  container: '[js-menu-drawer="container"]',
  tier3MenuToggle: '[js-menu-drawer="tier3MenuToggle"]',
  menuHandle: '[js-menu-drawer="menuHandle"]',
  menuNavBack: '[js-menu-drawer="menuNavBack"]',
  mainContent: '#MainContent',
  redirectModalToggle: '[js-menu-drawer="redirectModalToggle"]',
  currencySelector: '[js-currency-selector="selector"]'
};
/**
 * Instance cookies.
 */

const cookieSelector = {
  geolocation: 'redirect_geolocation',
  modal: 'redirect_modal',
  settings: 'redirect_store_settings'
};
/**
 * Instantiate menu drawer toggle.
 */

const MenuDrawer = Toggle({
  toggleSelector: 'menuDrawer'
});
/**
 * Component selectors.
 */

const menus = [...document.querySelectorAll(selectors.tier2MenuToggle), ...document.querySelectorAll(selectors.tier3MenuToggle)];
/**
 * Menu drawer section constructor.
 * Executes on page load as well as Theme Editor `section:load` events.
 */

register('menu-drawer', {
  /**
   * Set cached node selectors.
   */
  setNodeSelectors() {
    this.body = document.querySelector(selectors.body);
    this.container = document.querySelector(selectors.container);
    this.currencySelector = document.querySelectorAll(selectors.currencySelector);
    this.documentBody = document.querySelector(selectors.documentBody);
    this.menuHandle = document.querySelectorAll(selectors.menuHandle);
    this.menuNavBack = document.querySelector(selectors.menuNavBack);
    this.mainContent = document.querySelector(selectors.mainContent);
    this.redirectModalToggle = document.querySelector(selectors.redirectModalToggle);
  },

  /**
   * Initiate toggle events on all sub-menus.
   */
  setEventListeners() {
    [...menus].forEach(element => {
      on('click', element, event => this.onItemClick(event, element));
    });
    this.menuHandle.forEach(element => {
      on('click', element, event => this.onHandleClick(event, element));
    });
    on('click', this.menuNavBack, () => this.navigateBackMenu());

    if (this.currencySelector) {
      this.currencySelector.forEach(element => {
        on('change', element, event => this.handleCurrencyChange(event));
      });
    }
  },

  /**
   * Handle currency select change.
   * @param {Object} event - currency event.
   */
  handleCurrencyChange(event) {
    const selectedIndex = event.target.selectedIndex;
    const selectedCurrency = event.target[selectedIndex].getAttribute('value');
    window.location = "".concat(window.location.origin, "?currency=").concat(selectedCurrency);
  },

  /**
   * Update currency options to reflect store settings in user's cookie.
   * - Replace choice card templates.
   * - Update currency selector and re-render options based on selected.
   */
  renderCurrencySelection() {
    const storeCode = this.getDefaultStoreCode() === 'EU' ? 'FR' : this.getDefaultStoreCode();
    const matchingStore = Redirects().getMatchingStore(storeCode);
    const selectedCurrency = theme.features.currencyConverter ? Currency().getDefaultCurrencyCode() : Shopify.currency.active;
    this.currencySelector.forEach(selector => {
      selector.innerHTML = this.getOptionTemplate(this.getSupportedCurrencies(matchingStore));
    });
    this.updateSelectedCurrencyOption(selectedCurrency);
  },

  /**
   * Update currency selector to use default selected currency in settings.
   * @param {String} currencyCode - Currency to select.
   */
  updateSelectedCurrencyOption(currencyCode) {
    if (!currencyCode) {
      return;
    }

    const currencySelector = document.querySelector(selectors.currencySelector);
    const selectedOption = [...currencySelector.children].find(option => option.value === currencyCode);

    if (typeof selectedOption === 'undefined') {
      return;
    }

    this.currencySelector.forEach(selector => {
      selector.selectedIndex = selectedOption.index;
    });
  },

  /**
   * Get option templates from array.
   * @param {Array} options - Supported options based on matched store.
   * @returns {String}
   */
  getOptionTemplate(options) {
    return options.map(option => {
      return "\n        <option value=\"".concat(option.isoCode, "\">\n          ").concat(option.isoCode, " (").concat(option.symbol, ")\n        </option>");
    }).join('');
  },

  /**
   * Get store and country code using theme settings and cookies.
   * @returns {String}
   */
  getDefaultStoreCode() {
    if (typeof Cookies.getJSON(cookieSelector.settings) !== 'undefined') {
      return Cookies.getJSON(cookieSelector.settings).loc.toUpperCase();
    }

    return theme.store.storeCode;
  },

  /**
   * Returns the supported currencies for the store.
   * @param {Object} store - Store object from multi-store config.
   * @returns {Array}
   */
  getSupportedCurrencies(store) {
    if (theme.features.currencyConverter) {
      return store.supportedCurrencies.split(', ');
    }

    return theme.enabledCurrencies.map(currency => {
      return {
        isoCode: currency.iso_code,
        symbol: currency.symbol
      };
    });
  },

  /**
   * Handle back button even when clicked.
   */
  navigateBackMenu() {
    Frame.EventBus.emit('Toggle:shopNavigationMenu:close');
    this.menuNavBack.classList.add(cssClasses.hidden);
    this.body.classList.remove('move--left');
  },

  /**
   * Resets menu drawer to initial state after closing.
  */
  resetMenu() {
    this.body.classList.remove('move--left');
    this.menuNavBack.classList.add(cssClasses.hidden);
    Frame.EventBus.emit('Toggle:shopNavigationMenu:close');
  },

  /**
   * Handle toggle event on menu item click.
   * @param {Event} event - Click event.
   * @param {HTMLElement} element - The menu item.
   */
  onItemClick(event, element) {
    event.preventDefault();
    this.toggleElement(element);
    this.toggleElement(getSiblings(element)[0]);
  },

  /**
   * Emits menu handle on click.
   * @param {Event} event - Click event.
   * @param {HTMLElement} element - The menu item.
   */
  onHandleClick(event, element) {
    event.preventDefault();
    const handle = element.getAttribute('data-menu-handle');
    Frame.EventBus.emit('Toggle:shopNavigationMenu:open', handle);
    this.body.classList.add('move--left');
    this.menuNavBack.classList.remove(cssClasses.hidden);
  },

  /**
   * Set EventBus listeners for each instance.
   */
  setEventBusListeners() {
    Frame.EventBus.listen(['Toggle:menuDrawer:open'], () => disableBodyScroll(this.documentBody));
    Frame.EventBus.listen(['Toggle:menuDrawer:close'], () => enableBodyScroll(this.documentBody));
    Frame.EventBus.listen(['Toggle:menuDrawer:reset'], () => this.resetMenu());
  },

  /**
   * Check if item is active.
   * @param {HTMLElement} element - The menu item.
   * @returns {Boolean}
   */
  isItemActive(element) {
    return element.classList.contains(cssClasses.active);
  },

  /**
   * Toggle button state.
   * @param {HTMLElement} element - The toggled button.
   */
  toggleElement(element) {
    if (this.isItemActive(element)) {
      this.showMenu(element);
      return;
    }

    this.hideMenu(element);
  },

  /**
   * Show menu.
   * @param {HTMLElement} element - The item to show.
   */
  showMenu(element) {
    element.classList.remove(cssClasses.active);
  },

  /**
   * Hide menu.
   * @param {HTMLElement} element - The item to hide.
   */
  hideMenu(element) {
    element.classList.add(cssClasses.active);
  },

  /**
   * Initialize section.
   */
  init() {
    this.setNodeSelectors();
    MenuDrawer.init();
    this.setEventListeners();

    if (theme.features.redirectModal && this.redirectModalToggle) {
      this.setRedirectModalToggle();
    }

    this.renderCurrencySelection();
  },

  /**
   * Set redirect modal toggle events.
   */
  setRedirectModalToggle() {
    const modal = Modal({
      id: 'redirect-modal'
    });
    on('click', this.redirectModalToggle, event => {
      event.preventDefault();

      if (Shopify.shop === 'meermin-seconds.myshopify.com') {
        return;
      }

      modal.show('redirect-modal');
      Frame.EventBus.emit('Toggle:menuDrawer:close');
      this.resetMenu();
    });
  },

  /**
   * Event callback for Theme Editor `section:select` event.
   */
  onSelect() {
    MenuDrawer.open();
  },

  /**
   * Event callback for Theme Editor `shopify:section:deselect` event.
   */
  onDeselect() {
    MenuDrawer.close();
  },

  /**
   * Event callback for Theme Editor `section:load` event
   */
  onLoad() {
    this.init();
    this.setEventBusListeners();
  }

});